import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.mocap');
import { OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Service } from "src/libs/portal/season/service";
import { FaceLandmarker, HandLandmarker, FilesetResolver, DrawingUtils } from '@mediapipe/tasks-vision';

// ─── One Euro Filter ───
function createOneEuroFilter(freq: number, minCutoff: number, beta: number, dCutoff: number) {
    let xPrev: number | null = null;
    let dxPrev: number = 0;
    let tPrev: number | null = null;

    function alpha(cutoff: number, dt: number): number {
        const tau = 1.0 / (2 * Math.PI * cutoff);
        return 1.0 / (1.0 + tau / dt);
    }

    return {
        filter(value: number, timestamp?: number): number {
            if (xPrev === null || tPrev === null) {
                xPrev = value;
                tPrev = timestamp ?? performance.now() / 1000;
                return value;
            }
            const t = timestamp ?? performance.now() / 1000;
            const dt = t - tPrev;
            if (dt <= 0) return xPrev;
            const dx = (value - xPrev) / dt;
            const edx = alpha(dCutoff, dt) * dx + (1 - alpha(dCutoff, dt)) * dxPrev;
            const co = minCutoff + beta * Math.abs(edx);
            const filtered = alpha(co, dt) * value + (1 - alpha(co, dt)) * xPrev;
            xPrev = filtered;
            dxPrev = edx;
            tPrev = t;
            return filtered;
        },
        reset() { xPrev = null; dxPrev = 0; tPrev = null; },
    };
}
type OneEuroFilterInstance = ReturnType<typeof createOneEuroFilter>;

// ─── Constants ───
const BLENDSHAPE_CATEGORIES: { [key: string]: string[] } = {
    'Eyes': [
        'eyeBlinkLeft', 'eyeBlinkRight',
        'eyeLookDownLeft', 'eyeLookDownRight', 'eyeLookInLeft', 'eyeLookInRight',
        'eyeLookOutLeft', 'eyeLookOutRight', 'eyeLookUpLeft', 'eyeLookUpRight',
        'eyeSquintLeft', 'eyeSquintRight', 'eyeWideLeft', 'eyeWideRight',
    ],
    'Brows': ['browDownLeft', 'browDownRight', 'browInnerUp', 'browOuterUpLeft', 'browOuterUpRight'],
    'Mouth': [
        'jawOpen', 'jawForward', 'jawLeft', 'jawRight',
        'mouthClose', 'mouthFunnel', 'mouthPucker', 'mouthLeft', 'mouthRight',
        'mouthSmileLeft', 'mouthSmileRight', 'mouthFrownLeft', 'mouthFrownRight',
        'mouthDimpleLeft', 'mouthDimpleRight', 'mouthStretchLeft', 'mouthStretchRight',
        'mouthRollLower', 'mouthRollUpper', 'mouthShrugLower', 'mouthShrugUpper',
        'mouthPressLeft', 'mouthPressRight', 'mouthLowerDownLeft', 'mouthLowerDownRight',
        'mouthUpperUpLeft', 'mouthUpperUpRight',
    ],
    'Cheeks & Nose': ['cheekPuff', 'cheekSquintLeft', 'cheekSquintRight', 'noseSneerLeft', 'noseSneerRight'],
    'Tongue': ['tongueOut'],
};

const FINGER_LANDMARKS = {
    thumb: { mcp: 1, pip: 2, dip: 3, tip: 4 },
    index: { mcp: 5, pip: 6, dip: 7, tip: 8 },
    middle: { mcp: 9, pip: 10, dip: 11, tip: 12 },
    ring: { mcp: 13, pip: 14, dip: 15, tip: 16 },
    pinky: { mcp: 17, pip: 18, dip: 19, tip: 20 },
};
const FINGER_NAMES = ['thumb', 'index', 'middle', 'ring', 'pinky'] as const;

// ─── Interfaces ───
interface Vec3 { x: number; y: number; z: number; }
interface FingerData { name: string; curl: number; mcpAngle: number; pipAngle: number; dipAngle: number; }
interface HandTrackingData {
    handedness: string;
    landmarks: Vec3[];
    wrist: { position: Vec3; rotation: { pitch: number; yaw: number; roll: number } };
    fingers: FingerData[];
    gesture: string;
}

// Eye detail state
type EyeState = 'normal' | 'blink' | 'wink_left' | 'wink_right' | 'squint' | 'wide';
interface EyeDetail {
    state: EyeState;
    left: { openness: number; squint: number; wide: number; };
    right: { openness: number; squint: number; wide: number; };
}

// Mouth detail state
type MouthOpenness = 'closed' | 'slightly_open' | 'open' | 'wide_open';
type VowelShape = 'none' | 'a' | 'e' | 'i' | 'o' | 'u';
type LipShape = 'neutral' | 'funnel' | 'pucker' | 'smile' | 'frown' | 'press';
interface MouthDetail {
    openness: MouthOpenness;
    opennessValue: number;
    vowelShape: VowelShape;
    vowelConfidence: number;
    lipShape: LipShape;
    lipValues: { funnel: number; pucker: number; smile: number; frown: number; press: number; };
}

// ─── Eyebrow Detection ───
interface EyebrowData {
    left: { raise: number; furrow: number; };
    right: { raise: number; furrow: number; };
}

function detectEyebrows(bs: { [key: string]: number }): EyebrowData {
    return {
        left: {
            raise: round(Math.max(bs['browOuterUpLeft'] || 0, bs['browInnerUp'] || 0)),
            furrow: round(bs['browDownLeft'] || 0),
        },
        right: {
            raise: round(Math.max(bs['browOuterUpRight'] || 0, bs['browInnerUp'] || 0)),
            furrow: round(bs['browDownRight'] || 0),
        },
    };
}

// ─── Gaze/Eye Direction ───
interface GazeData {
    horizontal: number;  // -1 (left) to 1 (right)
    vertical: number;    // -1 (down) to 1 (up)
    left: { horizontal: number; vertical: number };
    right: { horizontal: number; vertical: number };
}

function computeGaze(bs: { [key: string]: number }): GazeData {
    // eyeLookInLeft = left eye looking toward nose (right), eyeLookOutLeft = looking away from nose (left)
    // eyeLookInRight = right eye looking toward nose (left), eyeLookOutRight = looking away from nose (right)
    const lInL = bs['eyeLookInLeft'] || 0;    // left eye looking right
    const lOutL = bs['eyeLookOutLeft'] || 0;   // left eye looking left
    const lUpL = bs['eyeLookUpLeft'] || 0;
    const lDownL = bs['eyeLookDownLeft'] || 0;
    const lInR = bs['eyeLookInRight'] || 0;   // right eye looking left
    const lOutR = bs['eyeLookOutRight'] || 0;  // right eye looking right
    const lUpR = bs['eyeLookUpRight'] || 0;
    const lDownR = bs['eyeLookDownRight'] || 0;

    // Left eye: positive = right, negative = left
    const leftH = lInL - lOutL;
    const leftV = lUpL - lDownL;
    // Right eye: positive = right, negative = left
    const rightH = lOutR - lInR;
    const rightV = lUpR - lDownR;

    return {
        horizontal: round((leftH + rightH) / 2),
        vertical: round((leftV + rightV) / 2),
        left: { horizontal: round(leftH), vertical: round(leftV) },
        right: { horizontal: round(rightH), vertical: round(rightV) },
    };
}

// ─── Finger spread computation ───
function computeFingerSpread(landmarks: Vec3[]): number[] {
    const tipIndices = [4, 8, 12, 16, 20];
    const spreads: number[] = [];
    for (let i = 0; i < tipIndices.length - 1; i++) {
        const a = landmarks[tipIndices[i]];
        const b = landmarks[tipIndices[i + 1]];
        const dx = a.x - b.x, dy = a.y - b.y, dz = a.z - b.z;
        spreads.push(round(Math.sqrt(dx * dx + dy * dy + dz * dz), 4));
    }
    return spreads;
}

// ─── Eye Detection ───
function detectEyeDetail(bs: { [key: string]: number }): EyeDetail {
    const blinkL = bs['eyeBlinkLeft'] || 0;
    const blinkR = bs['eyeBlinkRight'] || 0;
    const squintL = bs['eyeSquintLeft'] || 0;
    const squintR = bs['eyeSquintRight'] || 0;
    const wideL = bs['eyeWideLeft'] || 0;
    const wideR = bs['eyeWideRight'] || 0;

    // openness: 1 = fully open, 0 = closed
    const opennessL = Math.max(0, Math.min(1, 1 - blinkL));
    const opennessR = Math.max(0, Math.min(1, 1 - blinkR));

    let state: EyeState = 'normal';
    const blinkThreshold = 0.55;
    const winkAsymmetry = 0.35;
    const squintThreshold = 0.45;
    const wideThreshold = 0.4;
    const avgWide = (wideL + wideR) / 2;
    const avgSquint = (squintL + squintR) / 2;

    if (blinkL > blinkThreshold && blinkR > blinkThreshold) {
        state = 'blink';
    } else if (blinkL > blinkThreshold && blinkR < blinkThreshold - winkAsymmetry) {
        state = 'wink_left';
    } else if (blinkR > blinkThreshold && blinkL < blinkThreshold - winkAsymmetry) {
        state = 'wink_right';
    } else if (avgWide > wideThreshold) {
        state = 'wide';
    } else if (avgSquint > squintThreshold && blinkL < blinkThreshold && blinkR < blinkThreshold) {
        state = 'squint';
    }

    return {
        state,
        left: { openness: round(opennessL), squint: round(squintL), wide: round(wideL) },
        right: { openness: round(opennessR), squint: round(squintR), wide: round(wideR) },
    };
}

// ─── Mouth Detection ───
function detectMouthDetail(bs: { [key: string]: number }): MouthDetail {
    const jawOpen = bs['jawOpen'] || 0;
    const mouthClose = bs['mouthClose'] || 0;
    const mouthFunnel = bs['mouthFunnel'] || 0;
    const mouthPucker = bs['mouthPucker'] || 0;
    const smileL = bs['mouthSmileLeft'] || 0;
    const smileR = bs['mouthSmileRight'] || 0;
    const frownL = bs['mouthFrownLeft'] || 0;
    const frownR = bs['mouthFrownRight'] || 0;
    const pressL = bs['mouthPressLeft'] || 0;
    const pressR = bs['mouthPressRight'] || 0;
    const stretchL = bs['mouthStretchLeft'] || 0;
    const stretchR = bs['mouthStretchRight'] || 0;
    const lowerDownL = bs['mouthLowerDownLeft'] || 0;
    const lowerDownR = bs['mouthLowerDownRight'] || 0;
    const upperUpL = bs['mouthUpperUpLeft'] || 0;
    const upperUpR = bs['mouthUpperUpRight'] || 0;

    const smile = (smileL + smileR) / 2;
    const frown = (frownL + frownR) / 2;
    const press = (pressL + pressR) / 2;
    const stretch = (stretchL + stretchR) / 2;
    const lowerDown = (lowerDownL + lowerDownR) / 2;
    const upperUp = (upperUpL + upperUpR) / 2;
    const lipSeparation = (lowerDown + upperUp) / 2;

    // Openness levels
    let openness: MouthOpenness = 'closed';
    const effectiveOpen = jawOpen * (1 - mouthClose * 0.5);
    if (effectiveOpen > 0.5) openness = 'wide_open';
    else if (effectiveOpen > 0.25) openness = 'open';
    else if (effectiveOpen > 0.1) openness = 'slightly_open';

    // Vowel shape detection
    let vowelShape: VowelShape = 'none';
    let vowelConfidence = 0;
    const vowelScores: { shape: VowelShape; score: number }[] = [];

    // A: jaw open wide + funnel moderate + lips apart
    const scoreA = jawOpen * 0.5 + lipSeparation * 0.3 + (1 - mouthPucker) * 0.2;
    if (jawOpen > 0.2) vowelScores.push({ shape: 'a', score: scoreA });

    // E: mouth stretched horizontally + slight open
    const scoreE = stretch * 0.4 + smile * 0.3 + (1 - mouthFunnel) * 0.15 + (1 - mouthPucker) * 0.15;
    if (stretch > 0.15 || smile > 0.2) vowelScores.push({ shape: 'e', score: scoreE });

    // I: smile/stretch + narrow + slight open
    const scoreI = smile * 0.4 + stretch * 0.3 + (1 - jawOpen) * 0.15 + (1 - mouthFunnel) * 0.15;
    if (smile > 0.25 && jawOpen < 0.3) vowelScores.push({ shape: 'i', score: scoreI });

    // O: funnel + jaw open moderate + round lips
    const scoreO = mouthFunnel * 0.4 + jawOpen * 0.3 + (1 - smile) * 0.15 + (1 - stretch) * 0.15;
    if (mouthFunnel > 0.15 && jawOpen > 0.1) vowelScores.push({ shape: 'o', score: scoreO });

    // U: pucker + slight jaw + tight round
    const scoreU = mouthPucker * 0.45 + mouthFunnel * 0.25 + (1 - smile) * 0.15 + (1 - stretch) * 0.15;
    if (mouthPucker > 0.2) vowelScores.push({ shape: 'u', score: scoreU });

    if (vowelScores.length > 0 && effectiveOpen > 0.08) {
        vowelScores.sort((a, b) => b.score - a.score);
        vowelShape = vowelScores[0].shape;
        vowelConfidence = vowelScores[0].score;
    }

    // Lip shape
    let lipShape: LipShape = 'neutral';
    const lipCandidates: { shape: LipShape; score: number }[] = [
        { shape: 'funnel', score: mouthFunnel },
        { shape: 'pucker', score: mouthPucker },
        { shape: 'smile', score: smile },
        { shape: 'frown', score: frown },
        { shape: 'press', score: press },
    ];
    const best = lipCandidates.reduce((a, b) => b.score > a.score ? b : a);
    if (best.score > 0.25) lipShape = best.shape;

    return {
        openness,
        opennessValue: round(effectiveOpen),
        vowelShape,
        vowelConfidence: round(vowelConfidence),
        lipShape,
        lipValues: {
            funnel: round(mouthFunnel),
            pucker: round(mouthPucker),
            smile: round(smile),
            frown: round(frown),
            press: round(press),
        },
    };
}

// ─── Helpers ───
function detectExpression(bs: { [key: string]: number }, eyeDetail: EyeDetail, mouthDetail: MouthDetail): string {
    const jawOpen = bs['jawOpen'] || 0;
    const smile = ((bs['mouthSmileLeft'] || 0) + (bs['mouthSmileRight'] || 0)) / 2;

    // Priority-based expression
    if (eyeDetail.state === 'wide' && jawOpen > 0.3) return 'surprised';
    if (eyeDetail.state === 'blink') return 'blink';
    if (eyeDetail.state === 'wink_left') return 'wink_left';
    if (eyeDetail.state === 'wink_right') return 'wink_right';
    if (eyeDetail.state === 'squint' && smile > 0.3) return 'happy_squint';
    if (eyeDetail.state === 'squint') return 'squint';
    if (smile > 0.5) return 'smile';
    if (mouthDetail.openness === 'wide_open') return 'talk_wide';
    if (mouthDetail.openness === 'open' || mouthDetail.openness === 'slightly_open') return 'talk';
    if (mouthDetail.lipShape === 'frown') return 'sad';
    if (mouthDetail.lipShape === 'pucker') return 'pucker';
    return 'neutral';
}

function round(v: number, d: number = 3): number {
    const f = Math.pow(10, d);
    return Math.round(v * f) / f;
}

// ─── Component ───
@Component({
    selector: 'wiz-page-mocap',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/page.mocap/view.scss */
:host {
  display: block;
  width: 100%;
  height: 100vh;
}

video.webcam-mirror {
  transform: scaleX(-1);
}

canvas.webcam-mirror {
  transform: scaleX(-1);
}

@keyframes shimmer {
  0%, 100% {
    opacity: 0.8;
  }
  50% {
    opacity: 1;
  }
}`],
})
export class PageMocapComponent implements OnInit, OnDestroy {
    @ViewChild('videoEl') videoRef: ElementRef<HTMLVideoElement>;
    @ViewChild('canvasEl') canvasRef: ElementRef<HTMLCanvasElement>;
    @ViewChild('avatarVideoEl') avatarVideoRef: ElementRef<HTMLVideoElement>;

    // State
    public isCapturing = false;
    public isLoading = true;
    public fps = 0;
    public status = 'MediaPipe 모델 로딩 중...';
    public faceDetected = false;
    public handsDetected = 0;
    public settings = {
        targetFps: 30,
        smoothingFactor: 0.5,
        enableFace: true,
        enableHands: true,
        showBlendShapes: false,
        showHandDetails: false,
    };

    // Face
    public blendShapeCategories = BLENDSHAPE_CATEGORIES;
    public blendShapeCategoryKeys = Object.keys(BLENDSHAPE_CATEGORIES);
    public currentBlendShapes: { [key: string]: number } = {};
    public headPose: { pitch: number; yaw: number; roll: number; position: Vec3 } | null = null;
    public expression = 'neutral';
    public eyeDetail: EyeDetail = { state: 'normal', left: { openness: 1, squint: 0, wide: 0 }, right: { openness: 1, squint: 0, wide: 0 } };
    public mouthDetail: MouthDetail = { openness: 'closed', opennessValue: 0, vowelShape: 'none', vowelConfidence: 0, lipShape: 'neutral', lipValues: { funnel: 0, pucker: 0, smile: 0, frown: 0, press: 0 } };
    public eyebrowData: EyebrowData = { left: { raise: 0, furrow: 0 }, right: { raise: 0, furrow: 0 } };
    public gazeData: GazeData = { horizontal: 0, vertical: 0, left: { horizontal: 0, vertical: 0 }, right: { horizontal: 0, vertical: 0 } };

    // Hands
    public leftHandData: HandTrackingData | null = null;
    public rightHandData: HandTrackingData | null = null;
    public fingerNames = FINGER_NAMES;

    // Unity (via Socket.IO relay)
    public unityConnected = false;
    public unityStatus = '';
    public framesSent = 0;
    public showCharacter = false;
    private socket: any = null;

    // WebRTC
    public rtcConnected = false;
    public rtcStatus = '';
    private peerConnection: RTCPeerConnection | null = null;
    private rtcConnectionId = '';
    private rtcTimeout: any = null;

    // Diagnostics
    public signalingLog: string[] = [];
    public relayAlive = false;
    private relayPingInterval: any = null;
    private pendingIceCandidates: any[] = [];

    // Private
    private faceLandmarker: FaceLandmarker | null = null;
    private handLandmarker: HandLandmarker | null = null;

    private stream: MediaStream | null = null;
    private animFrameId = 0;
    private lastFrameTime = 0;
    private frameCount = 0;
    private fpsInterval: any = null;
    private headFilters: { pitch: OneEuroFilterInstance; yaw: OneEuroFilterInstance; roll: OneEuroFilterInstance } | null = null;
    private blendShapeFilters: Map<string, OneEuroFilterInstance> = new Map();
    private lastSendTime = 0;
    private noseY = 0.5;

    constructor(@Inject( Service) public service: Service) { }

    public async ngOnInit() {
        await this.service.init();
        await this.initMediaPipe();
        this.isLoading = false;
        this.status = '카메라 켜기를 눌러주세요';
        await this.service.render();
    }

    public ngOnDestroy() {
        this.stopCapture();
        this.closeRTC();
        this.disconnectRelay();
        if (this.fpsInterval) clearInterval(this.fpsInterval);
        if (this.relayPingInterval) clearInterval(this.relayPingInterval);
        if (this.faceLandmarker) { this.faceLandmarker.close(); this.faceLandmarker = null; }
        if (this.handLandmarker) { this.handLandmarker.close(); this.handLandmarker = null; }

    }

    // ─── Diagnostics ───

    private addSignalingLog(msg: string) {
        const ts = new Date().toLocaleTimeString();
        this.signalingLog.push(`[${ts}] ${msg}`);
        if (this.signalingLog.length > 30) this.signalingLog = this.signalingLog.slice(-30);
        console.log(`[MoCap] ${msg}`);
    }

    private startRelayPing() {
        this.relayPingInterval = setInterval(() => {
            if (this.socket && this.unityConnected) {
                this.socket.emit('relay_ping', {});
            }
        }, 5000);
    }

    // ─── Unity Relay (Socket.IO via WIZ server) ───

    private connectRelay() {
        this.unityStatus = '서버 연결 중...';
        this.signalingLog = [];
        this.addSignalingLog('Socket.IO 릴레이 연결 시작');

        try {
            this.socket = wiz.socket();
        } catch (e) {
            this.addSignalingLog(`wiz.socket() 실패: ${e}`);
            this.unityStatus = 'Socket.IO 연결 실패';
            return;
        }

        this.socket.on('connect', async () => {
            this.addSignalingLog('Socket.IO 연결됨');
            this.unityStatus = 'Unity 연결 대기...';
            this.framesSent = 0;
            this.startRelayPing();
            await this.service.render();
        });

        this.socket.on('disconnect', async () => {
            this.addSignalingLog('Socket.IO 끊김');
            this.unityConnected = false;
            this.relayAlive = false;
            this.unityStatus = '연결 끊김';
            if (this.relayPingInterval) { clearInterval(this.relayPingInterval); this.relayPingInterval = null; }
            if (this.showCharacter) {
                this.showCharacter = false;
                this.closeRTC();
            }
            await this.service.render();
        });

        // Unity 연결 상태 (서버에서 전달)
        this.socket.on('unity_status', async (data: any) => {
            this.addSignalingLog(`unity_status: connected=${data.connected}`);
            this.unityConnected = !!data.connected;
            if (data.connected) {
                this.unityStatus = 'Unity 연결됨';
            } else {
                this.unityStatus = data.error ? `Unity 연결 실패: ${data.error}` : 'Unity 연결 끊김';
                if (this.showCharacter) {
                    this.showCharacter = false;
                    this.closeRTC();
                }
            }
            await this.service.render();
        });

        // 릴레이 진단
        this.socket.on('relay_pong', async (msg: any) => {
            this.relayAlive = !!msg.ok;
            if (!msg.ok) this.addSignalingLog(`relay_pong 실패: ${msg.error}`);
            await this.service.render();
        });

        this.socket.on('relay_status', async (msg: any) => {
            this.addSignalingLog(`릴레이: Unity=${msg.unity_connected ? '연결' : '미연결'}, 브라우저=${msg.browsers?.length}개`);
            if (msg.event_log) {
                for (const log of msg.event_log.slice(-5)) {
                    this.addSignalingLog(`[서버] ${log}`);
                }
            }
            await this.service.render();
        });

        // Unity → Browser: 캐릭터 ack
        this.socket.on('character_ack', async (msg: any) => {
            this.addSignalingLog(`character_ack: visible=${msg.visible}${msg.error ? ', error=' + msg.error : ''}`);
            // showCharacter는 사용자가 명시적으로 toggleCharacterView()를 호출한 경우에만 true로 설정됨
            // character_ack로 인한 자동 활성화를 방지 — OFF 응답만 반영
            if (!msg.visible) {
                this.showCharacter = false;
                this.closeRTC();
            }
            await this.service.render();
        });

        // Unity → Browser: WebRTC 연결 시작 신호
        this.socket.on('webrtc_connect', async (msg: any) => {
            this.addSignalingLog('webrtc_connect 수신 ← Unity');
            this.rtcStatus = 'WebRTC 연결 준비 중...';
            await this.service.render();
        });

        // Unity → Browser: WebRTC offer
        this.socket.on('webrtc_offer', async (msg: any) => {
            this.addSignalingLog(`webrtc_offer 수신 ← Unity (SDP ${msg.sdp?.length || 0}bytes)`);
            if (!msg.sdp) {
                this.addSignalingLog('webrtc_offer 무시: SDP 비어있음');
                return;
            }
            await this.handleRTCOffer(msg.connectionId, msg.sdp);
        });

        // Unity → Browser: ICE candidate (통합 webrtc_ice 이벤트)
        this.socket.on('webrtc_ice', async (msg: any) => {
            this.addSignalingLog(`webrtc_ice 수신 ← Unity`);
            if (!this.peerConnection) {
                this.addSignalingLog('ICE 버퍼링: PeerConnection 아직 없음');
                this.pendingIceCandidates.push(msg);
                return;
            }
            await this.handleRTCIce(msg);
        });
    }

    private disconnectRelay() {
        this.closeRTC();
        if (this.relayPingInterval) { clearInterval(this.relayPingInterval); this.relayPingInterval = null; }
        if (this.socket) {
            try { this.socket.disconnect(); } catch (e) { }
            this.socket = null;
        }
        this.unityConnected = false;
        this.relayAlive = false;
        this.unityStatus = '';
        this.framesSent = 0;
        this.showCharacter = false;
    }

    public async toggleCharacterView() {
        if (!this.socket || !this.unityConnected) return;
        const next = !this.showCharacter;
        this.addSignalingLog(`character_${next ? 'on' : 'off'} → Unity`);
        this.socket.emit(next ? 'character_on' : 'character_off', {});
        this.showCharacter = next;
        if (!next) {
            this.closeRTC();
        } else {
            this.rtcStatus = 'Unity WebRTC Offer 대기 중...';
        }
        await this.service.render();
    }

    public async retryWebRTC() {
        if (!this.socket || !this.unityConnected || !this.showCharacter) return;
        this.addSignalingLog('WebRTC 재시도: character_off → character_on');
        this.closeRTC();
        this.socket.emit('character_off', {});
        await new Promise(r => setTimeout(r, 1000));
        if (this.unityConnected) {
            this.socket.emit('character_on', {});
            this.rtcStatus = 'Unity WebRTC Offer 대기 중...';
            await this.service.render();
        }
    }

    private clearRTCTimeout() {
        if (this.rtcTimeout) {
            clearTimeout(this.rtcTimeout);
            this.rtcTimeout = null;
        }
    }

    // ─── WebRTC (receive video from Unity) ───

    private createPeerConnection() {
        this.closeRTC();
        this.pendingIceCandidates = [];
        this.addSignalingLog('RTCPeerConnection 생성');
        const pc = new RTCPeerConnection({
            iceServers: [
                { urls: 'stun:stun.l.google.com:19302' },
                { urls: 'stun:stun1.l.google.com:19302' },
            ],
        });

        pc.ontrack = async (event: RTCTrackEvent) => {
            this.addSignalingLog(`ontrack: kind=${event.track.kind}, streams=${event.streams.length}, state=${event.track.readyState}`);
            const video = this.avatarVideoRef?.nativeElement;
            if (!video) {
                this.addSignalingLog('ontrack 실패: avatarVideoRef 없음!');
                return;
            }
            if (event.streams?.[0]) {
                video.srcObject = event.streams[0];
                this.addSignalingLog(`video.srcObject 설정 (tracks: ${event.streams[0].getTracks().map(t => `${t.kind}:${t.readyState}`).join(', ')})`);
            } else if (event.track) {
                const stream = new MediaStream([event.track]);
                video.srcObject = stream;
                this.addSignalingLog(`fallback MediaStream: ${event.track.kind}`);
            }
            this.rtcConnected = true;
            this.rtcStatus = 'WebRTC 스트리밍 중';
            this.clearRTCTimeout();
            await this.service.render();

            setTimeout(async () => {
                try {
                    await video.play();
                    this.addSignalingLog(`video.play() 성공 (${video.videoWidth}x${video.videoHeight})`);
                } catch (e) {
                    this.addSignalingLog(`video.play() 실패: ${e}`);
                }
            }, 200);
        };

        pc.onicecandidate = (event: RTCPeerConnectionIceEvent) => {
            if (event.candidate && this.socket) {
                this.addSignalingLog(`ICE → Unity: ${event.candidate.candidate.substring(0, 40)}...`);
                this.socket.emit('webrtc_ice', {
                    connectionId: this.rtcConnectionId,
                    candidate: event.candidate.candidate,
                    sdpMid: event.candidate.sdpMid,
                    sdpMLineIndex: event.candidate.sdpMLineIndex,
                });
            } else if (!event.candidate) {
                this.addSignalingLog('ICE gathering 완료');
            }
        };

        pc.oniceconnectionstatechange = () => {
            this.addSignalingLog(`ICE state: ${pc.iceConnectionState}`);
        };

        pc.onconnectionstatechange = () => {
            this.addSignalingLog(`Connection state: ${pc.connectionState}`);
            if (pc.connectionState === 'connected') {
                this.rtcConnected = true;
                this.rtcStatus = 'WebRTC 연결됨';
                this.clearRTCTimeout();
                this.service.render();
            } else if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
                this.rtcConnected = false;
                this.rtcStatus = `WebRTC ${pc.connectionState}`;
                this.service.render();
            }
        };

        this.peerConnection = pc;
        return pc;
    }

    private async handleRTCOffer(connectionId: string, sdp: string) {
        try {
            this.rtcConnectionId = connectionId || '';
            const pc = this.createPeerConnection();
            this.rtcStatus = 'SDP Offer 수신, Answer 생성 중...';
            await this.service.render();

            this.addSignalingLog('setRemoteDescription (offer)');
            await pc.setRemoteDescription({ type: 'offer', sdp });

            // 버퍼링된 ICE candidate 처리
            if (this.pendingIceCandidates.length > 0) {
                this.addSignalingLog(`버퍼링된 ICE ${this.pendingIceCandidates.length}개 처리`);
                for (const ice of this.pendingIceCandidates) {
                    await this.handleRTCIce(ice);
                }
                this.pendingIceCandidates = [];
            }

            this.addSignalingLog('createAnswer');
            const answer = await pc.createAnswer();

            this.addSignalingLog('setLocalDescription (answer)');
            await pc.setLocalDescription(answer);

            if (this.socket) {
                this.socket.emit('webrtc_answer', {
                    connectionId: this.rtcConnectionId,
                    sdp: answer.sdp,
                });
                this.addSignalingLog('webrtc_answer → Unity');
            }

            this.rtcStatus = 'Answer 전송됨, ICE 교환 중...';
            await this.service.render();
        } catch (e) {
            this.addSignalingLog(`handleRTCOffer 에러: ${e}`);
            this.rtcStatus = `Offer 처리 실패: ${e}`;
            await this.service.render();
        }
    }

    private async handleRTCIce(msg: any) {
        if (!this.peerConnection) {
            this.pendingIceCandidates.push(msg);
            return;
        }
        if (!msg.candidate) return;
        try {
            await this.peerConnection.addIceCandidate({
                candidate: msg.candidate,
                sdpMid: msg.sdpMid ?? null,
                sdpMLineIndex: msg.sdpMLineIndex ?? null,
            });
        } catch (e) {
            this.addSignalingLog(`ICE candidate 추가 실패: ${e}`);
        }
    }

    private closeRTC() {
        this.clearRTCTimeout();
        if (this.peerConnection) {
            console.log('[MoCap] RTCPeerConnection 닫기');
            this.peerConnection.close();
            this.peerConnection = null;
        }
        this.rtcConnected = false;
        this.rtcStatus = '';
        const video = this.avatarVideoRef?.nativeElement;
        if (video) video.srcObject = null;
    }

    private sendToUnity() {
        if (!this.socket || !this.unityConnected) return;
        const now = Date.now();
        if (now - this.lastSendTime < 1000 / this.settings.targetFps) return;
        this.lastSendTime = now;

        const blendshapes: { name: string; value: number }[] = [];
        for (const [name, value] of Object.entries(this.currentBlendShapes)) {
            if (name === '_neutral') continue;
            blendshapes.push({ name, value: round(value) });
        }

        const handJson = (h: HandTrackingData | null) => ({
            detected: !!h,
            wristPosition: { x: round(h?.wrist?.position?.x ?? 0, 4), y: round(h?.wrist?.position?.y ?? 0, 4), z: round(h?.wrist?.position?.z ?? 0, 4) },
            wristRotation: { pitch: round(h?.wrist?.rotation?.pitch ?? 0, 1), yaw: round(h?.wrist?.rotation?.yaw ?? 0, 1), roll: round(h?.wrist?.rotation?.roll ?? 0, 1) },
            fingers: h ? h.fingers.map(f => ({ name: f.name, curl: round(f.curl), spread: 0 })) : [],
            gesture: h?.gesture ?? '',
            fingerSpreads: h ? computeFingerSpread(h.landmarks) : [],
        });

        const data = {
            head: {
                pitch: round(this.headPose?.pitch ?? 0, 1),
                yaw: round(this.headPose?.yaw ?? 0, 1),
                roll: round(this.headPose?.roll ?? 0, 1),
                position: {
                    x: round(this.headPose?.position?.x ?? 0, 4),
                    y: round(this.headPose?.position?.y ?? 0, 4),
                    z: round(this.headPose?.position?.z ?? 0, 4),
                },
            },
            blendshapes,
            expression: this.expression,
            eyeDetail: this.eyeDetail,
            gaze: this.gazeData,
            mouthDetail: {
                openness: this.mouthDetail.openness,
                opennessValue: this.mouthDetail.opennessValue,
                vowelShape: this.mouthDetail.vowelShape,
                vowelConfidence: this.mouthDetail.vowelConfidence,
                lipShape: this.mouthDetail.lipShape,
            },
            eyebrows: this.eyebrowData,
            leftHand: handJson(this.leftHandData),
            rightHand: handJson(this.rightHandData),
        };

        this.socket.emit('tracking_data', data);
        this.framesSent++;
    }

    // ─── Capture ───

    public async toggleCapture() {
        if (this.isCapturing) {
            // Stop everything: virtual mode → relay → camera
            if (this.showCharacter) {
                this.showCharacter = false;
                if (this.socket && this.unityConnected) {
                    this.socket.emit('character_off', {});
                }
                this.closeRTC();
            }
            this.disconnectRelay();
            this.stopCapture();
        } else {
            await this.startCapture();
            // Auto-connect Unity relay after camera starts
            if (this.isCapturing) {
                this.connectRelay();
            }
        }
        await this.service.render();
    }

    public async toggleBlendShapes() {
        this.settings.showBlendShapes = !this.settings.showBlendShapes;
        await this.service.render();
    }

    public async toggleHandDetails() {
        this.settings.showHandDetails = !this.settings.showHandDetails;
        await this.service.render();
    }

    private async initMediaPipe() {
        try {
            console.log('[MoCap] WASM 로딩 시작...');
            const vision = await FilesetResolver.forVisionTasks(
                'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm'
            );
            console.log('[MoCap] WASM 로드 완료, 모델 로딩 시작...');

            // Face + Hand 모델은 필수, Pose는 선택적 (병렬 로드)
            const [faceResult, handResult] = await Promise.all([
                FaceLandmarker.createFromOptions(vision, {
                    baseOptions: {
                        modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task',
                        delegate: 'GPU',
                    },
                    runningMode: 'VIDEO', numFaces: 1,
                    outputFaceBlendshapes: true, outputFacialTransformationMatrixes: true,
                }),
                HandLandmarker.createFromOptions(vision, {
                    baseOptions: {
                        modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task',
                        delegate: 'GPU',
                    },
                    runningMode: 'VIDEO', numHands: 2,
                }),
            ]);
            this.faceLandmarker = faceResult;
            this.handLandmarker = handResult;
            console.log('[MoCap] Face + Hand 모델 로드 완료');
            this.initFilters();
            this.status = '준비 완료';

        } catch (e: any) {
            console.error('[MoCap] 모델 로드 실패:', e);
            this.status = `모델 로드 실패: ${e.message}`;
        }
    }

    private initFilters() {
        const freq = this.settings.targetFps;
        const beta = this.settings.smoothingFactor;
        this.headFilters = {
            pitch: createOneEuroFilter(freq, 1.0, beta, 1.0),
            yaw: createOneEuroFilter(freq, 1.0, beta, 1.0),
            roll: createOneEuroFilter(freq, 1.0, beta, 1.0),
        };
        this.blendShapeFilters.clear();
        for (const cats of Object.values(BLENDSHAPE_CATEGORIES)) {
            for (const name of cats) {
                this.blendShapeFilters.set(name, createOneEuroFilter(freq, 1.5, beta * 0.8, 1.0));
            }
        }
    }

    private async startCapture() {
        try {
            console.log('[MoCap] 카메라 시작...');
            this.stream = await navigator.mediaDevices.getUserMedia({
                video: { width: 640, height: 480, facingMode: 'user' }, audio: false,
            });
            console.log('[MoCap] 카메라 스트림 획득 완료');
            const video = this.videoRef.nativeElement;
            video.srcObject = this.stream;
            await video.play();
            console.log(`[MoCap] 비디오 재생 시작 (${video.videoWidth}x${video.videoHeight})`);
            this.isCapturing = true;
            this.status = '캡처 중...';
            this.frameCount = 0;
            this.fps = 0;
            this.initFilters();
            this.fpsInterval = setInterval(() => { this.fps = this.frameCount; this.frameCount = 0; }, 1000);
            this.processFrame();
        } catch (e: any) {
            console.error('[MoCap] 카메라 접근 실패:', e);
            this.status = `카메라 접근 실패: ${e.message}`;
            if (this.stream) { this.stream.getTracks().forEach(t => t.stop()); this.stream = null; }
        }
    }

    private stopCapture() {
        this.isCapturing = false;
        this.status = '캡처 중지됨';
        if (this.animFrameId) { cancelAnimationFrame(this.animFrameId); this.animFrameId = 0; }
        if (this.fpsInterval) { clearInterval(this.fpsInterval); this.fpsInterval = null; }
        if (this.stream) { this.stream.getTracks().forEach(t => t.stop()); this.stream = null; }
    }

    private processFrame() {
        if (!this.isCapturing) return;
        const now = performance.now();
        const elapsed = now - this.lastFrameTime;
        const interval = 1000 / this.settings.targetFps;
        if (elapsed >= interval) {
            this.lastFrameTime = now - (elapsed % interval);
            try {
                this.detectAndDraw(now);
            } catch (e) {
                console.error('[MoCap] detectAndDraw 에러:', e);
            }
            this.frameCount++;
        }
        this.animFrameId = requestAnimationFrame(() => this.processFrame());
    }

    private detectAndDraw(timestamp: number) {
        const video = this.videoRef.nativeElement;
        const canvas = this.canvasRef.nativeElement;
        const ctx = canvas.getContext('2d')!;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        const drawingUtils = new DrawingUtils(ctx);
        const ts = timestamp / 1000;

        if (this.settings.enableFace && this.faceLandmarker) {
            const faceResult = this.faceLandmarker.detectForVideo(video, timestamp);
            if (faceResult.faceLandmarks && faceResult.faceLandmarks.length > 0) {
                this.faceDetected = true;
                const landmarks = faceResult.faceLandmarks[0];
                if (landmarks[1]) this.noseY = landmarks[1].y;
                for (const lm of faceResult.faceLandmarks) {
                    drawingUtils.drawConnectors(lm, FaceLandmarker.FACE_LANDMARKS_TESSELATION, { color: '#C0C0C070', lineWidth: 0.5 });
                    drawingUtils.drawConnectors(lm, FaceLandmarker.FACE_LANDMARKS_RIGHT_EYE, { color: '#30FF30', lineWidth: 1 });
                    drawingUtils.drawConnectors(lm, FaceLandmarker.FACE_LANDMARKS_LEFT_EYE, { color: '#30FF30', lineWidth: 1 });
                    drawingUtils.drawConnectors(lm, FaceLandmarker.FACE_LANDMARKS_RIGHT_IRIS, { color: '#00BBFF', lineWidth: 1 });
                    drawingUtils.drawConnectors(lm, FaceLandmarker.FACE_LANDMARKS_LEFT_IRIS, { color: '#00BBFF', lineWidth: 1 });
                    drawingUtils.drawConnectors(lm, FaceLandmarker.FACE_LANDMARKS_FACE_OVAL, { color: '#E0E0E0', lineWidth: 1 });
                    drawingUtils.drawConnectors(lm, FaceLandmarker.FACE_LANDMARKS_LIPS, { color: '#FF3030', lineWidth: 1 });
                }
                if (faceResult.faceBlendshapes && faceResult.faceBlendshapes.length > 0) {
                    const smoothedBS: { [key: string]: number } = {};
                    for (const bs of faceResult.faceBlendshapes[0].categories) {
                        const filter = this.blendShapeFilters.get(bs.categoryName);
                        smoothedBS[bs.categoryName] = filter ? filter.filter(bs.score, ts) : bs.score;
                    }
                    this.currentBlendShapes = smoothedBS;
                    this.eyeDetail = detectEyeDetail(smoothedBS);
                    this.mouthDetail = detectMouthDetail(smoothedBS);
                    this.eyebrowData = detectEyebrows(smoothedBS);
                    this.gazeData = computeGaze(smoothedBS);
                    this.expression = detectExpression(smoothedBS, this.eyeDetail, this.mouthDetail);
                }
                if (faceResult.facialTransformationMatrixes && faceResult.facialTransformationMatrixes.length > 0) {
                    const raw = this.extractHeadPoseRaw(faceResult.facialTransformationMatrixes[0].data);
                    if (this.headFilters) {
                        raw.pitch = this.headFilters.pitch.filter(raw.pitch, ts);
                        raw.yaw = this.headFilters.yaw.filter(raw.yaw, ts);
                        raw.roll = this.headFilters.roll.filter(raw.roll, ts);
                    }
                    this.headPose = raw;
                }
            } else {
                this.faceDetected = false;
                this.headPose = null;
                this.currentBlendShapes = {};
                this.expression = 'neutral';
            }
        }

        if (this.settings.enableHands && this.handLandmarker) {
            const handResult = this.handLandmarker.detectForVideo(video, timestamp);
            this.leftHandData = null;
            this.rightHandData = null;
            if (handResult.landmarks && handResult.landmarks.length > 0) {
                this.handsDetected = handResult.landmarks.length;
                for (let i = 0; i < handResult.landmarks.length; i++) {
                    const lm = handResult.landmarks[i];
                    const handedness = handResult.handednesses[i][0].categoryName;
                    const color = handedness === 'Left' ? '#FF6600' : '#00AAFF';
                    drawingUtils.drawConnectors(lm, HandLandmarker.HAND_CONNECTIONS, { color, lineWidth: 2 });
                    drawingUtils.drawLandmarks(lm, { color, lineWidth: 1, radius: 2 });
                    const handData = this.processHandData(lm, handedness);
                    if (handedness === 'Left') this.rightHandData = handData;
                    else this.leftHandData = handData;
                }
            } else {
                this.handsDetected = 0;
            }
        }

        // Send to Unity via Socket.IO relay
        if (this.unityConnected) {
            this.sendToUnity();
        }
    }

    // ─── Math Utilities ───

    private processHandData(landmarks: any[], handedness: string): HandTrackingData {
        const lm: Vec3[] = landmarks.map((l: any) => ({ x: l.x, y: l.y, z: l.z }));
        const wristPos = lm[0];
        const wristRotation = this.computeWristRotation(lm);
        const fingers: FingerData[] = [];
        for (const name of FINGER_NAMES) {
            const idx = FINGER_LANDMARKS[name];
            const mcpAngle = this.angleBetweenPoints(lm[0], lm[idx.mcp], lm[idx.pip]);
            const pipAngle = this.angleBetweenPoints(lm[idx.mcp], lm[idx.pip], lm[idx.dip]);
            const dipAngle = this.angleBetweenPoints(lm[idx.pip], lm[idx.dip], lm[idx.tip]);
            const curl = Math.max(0, Math.min(1, 1 - (pipAngle + dipAngle) / 360));
            fingers.push({ name, curl, mcpAngle, pipAngle, dipAngle });
        }
        return { handedness, landmarks: lm, wrist: { position: wristPos, rotation: wristRotation }, fingers, gesture: this.detectGesture(fingers) };
    }

    private angleBetweenPoints(a: Vec3, b: Vec3, c: Vec3): number {
        const ba = { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
        const bc = { x: c.x - b.x, y: c.y - b.y, z: c.z - b.z };
        const dot = ba.x * bc.x + ba.y * bc.y + ba.z * bc.z;
        const magBA = Math.sqrt(ba.x * ba.x + ba.y * ba.y + ba.z * ba.z);
        const magBC = Math.sqrt(bc.x * bc.x + bc.y * bc.y + bc.z * bc.z);
        if (magBA === 0 || magBC === 0) return 0;
        return Math.acos(Math.max(-1, Math.min(1, dot / (magBA * magBC)))) * (180 / Math.PI);
    }

    private computeWristRotation(lm: Vec3[]): { pitch: number; yaw: number; roll: number } {
        const wrist = lm[0], indexMcp = lm[5], pinkyMcp = lm[17];
        const mid = { x: (indexMcp.x + pinkyMcp.x) / 2, y: (indexMcp.y + pinkyMcp.y) / 2, z: (indexMcp.z + pinkyMcp.z) / 2 };
        const fwd = this.normalize({ x: mid.x - wrist.x, y: mid.y - wrist.y, z: mid.z - wrist.z });
        const right = this.normalize({ x: pinkyMcp.x - indexMcp.x, y: pinkyMcp.y - indexMcp.y, z: pinkyMcp.z - indexMcp.z });
        const up = this.cross(fwd, right);
        return {
            pitch: Math.asin(-fwd.y) * (180 / Math.PI),
            yaw: Math.atan2(fwd.x, fwd.z) * (180 / Math.PI),
            roll: Math.atan2(right.y, up.y) * (180 / Math.PI),
        };
    }

    private normalize(v: Vec3): Vec3 {
        const mag = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
        return mag === 0 ? { x: 0, y: 0, z: 0 } : { x: v.x / mag, y: v.y / mag, z: v.z / mag };
    }

    private cross(a: Vec3, b: Vec3): Vec3 {
        return { x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x };
    }

    private detectGesture(fingers: FingerData[]): string {
        const c = fingers.map(f => f.curl);
        if (c.every(v => v > 0.6)) return 'fist';
        if (c.every(v => v < 0.3)) return 'open';
        if (c[1] < 0.3 && c[2] > 0.5 && c[3] > 0.6 && c[4] > 0.6) return 'pointing';
        if (c[1] < 0.3 && c[2] < 0.3 && c[3] > 0.6 && c[4] > 0.6) return 'peace';
        return 'unknown';
    }

    private extractHeadPoseRaw(matrixData: Float32Array | number[]): any {
        const m = matrixData;
        const r00 = m[0], r10 = m[1], r20 = m[2];
        const r21 = m[6], r22 = m[10], r12 = m[9], r11 = m[5];
        const sy = Math.sqrt(r00 * r00 + r10 * r10);
        let pitch: number, yaw: number, roll: number;
        if (sy > 1e-6) {
            pitch = Math.atan2(r21, r22) * (180 / Math.PI);
            yaw = Math.atan2(-r20, sy) * (180 / Math.PI);
            roll = Math.atan2(r10, r00) * (180 / Math.PI);
        } else {
            pitch = Math.atan2(-r12, r11) * (180 / Math.PI);
            yaw = Math.atan2(-r20, sy) * (180 / Math.PI);
            roll = 0;
        }
        return {
            pitch: Math.max(-90, Math.min(90, pitch)),
            yaw: Math.max(-90, Math.min(90, yaw)),
            roll: Math.max(-90, Math.min(90, roll)),
            position: { x: m[12] || 0, y: m[13] || 0, z: m[14] || 0 },
        };
    }
}

export default PageMocapComponent;